import { useMemo, useState } from "react";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";

import uploadManager, { useUploads } from "../../uploadManager";
import { primaryTint } from "../../surfaceTints";

const activeStatuses = new Set(["queued", "uploading"]);
const finishedStatuses = new Set(["complete", "cancelled", "error"]);

function formatBytes(bytes) {
    if (!bytes) {
        return "0 MB";
    }

    const units = ["B", "KB", "MB", "GB", "TB"];
    const unitIndex = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1,
    );
    const value = bytes / 1024 ** unitIndex;

    return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(
        1,
    )} ${units[unitIndex]}`;
}

function UploadFileIcon({ fileType }) {
    if (fileType?.includes("video")) {
        return <MovieOutlinedIcon fontSize="small" />;
    }

    if (fileType?.includes("audio")) {
        return <AudiotrackOutlinedIcon fontSize="small" />;
    }

    if (fileType?.includes("image")) {
        return <ImageOutlinedIcon fontSize="small" />;
    }

    if (fileType === "application/pdf") {
        return <PictureAsPdfOutlinedIcon fontSize="small" />;
    }

    return <InsertDriveFileOutlinedIcon fontSize="small" />;
}

function UploadStatusIcon({ status }) {
    if (status === "complete") {
        return (
            <CheckCircleOutlineIcon
                fontSize="small"
                sx={{ color: "primary.main" }}
            />
        );
    }

    if (status === "error") {
        return <ErrorOutlineIcon fontSize="small" color="error" />;
    }

    if (status === "cancelled") {
        return (
            <HighlightOffOutlinedIcon
                fontSize="small"
                sx={{ color: "text.disabled" }}
            />
        );
    }

    return null;
}

function UploadRow({ upload, onCancel, onRetry, onRemove }) {
    const isActive = activeStatuses.has(upload.status);
    const progress = Math.min(100, Math.max(0, upload.progress || 0));
    const statusMessage =
        upload.status === "queued"
            ? "Waiting to upload"
            : upload.status === "uploading"
              ? "Uploading"
              : upload.status === "complete"
                ? "Upload complete"
                : upload.status === "cancelled"
                  ? "Upload cancelled"
                  : upload.errorMessage || "Upload failed";

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                }}
            >
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 1.75,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: isActive ? "primary.main" : "text.secondary",
                        backgroundColor: isActive
                            ? primaryTint
                            : "background.subtle",
                    }}
                >
                    <UploadFileIcon fileType={upload.file.type} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontWeight: 800,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {upload.file.name}
                        </Typography>
                        <UploadStatusIcon status={upload.status} />
                    </Box>
                    <Typography
                        variant="caption"
                        color={
                            upload.status === "error"
                                ? "error"
                                : "text.secondary"
                        }
                        sx={{ display: "block" }}
                    >
                        {statusMessage}
                    </Typography>
                    {upload.file.size > 0 && (
                        <Typography variant="caption" color="text.disabled">
                            {formatBytes(upload.file.size)}
                        </Typography>
                    )}
                </Box>

                {isActive && (
                    <Tooltip title="Cancel upload">
                        <IconButton
                            aria-label={`Cancel ${upload.file.name}`}
                            size="small"
                            onClick={() => onCancel(upload.file.key)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {upload.status === "error" &&
                    (upload.tusUpload || upload.beginFn) && (
                        <Tooltip title="Retry upload">
                            <IconButton
                                aria-label={`Retry ${upload.file.name}`}
                                size="small"
                                color="primary"
                                onClick={() => onRetry(upload.file.key)}
                            >
                                <ReplayOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                {!isActive && (
                    <Tooltip title="Remove from list">
                        <IconButton
                            aria-label={`Remove ${upload.file.name}`}
                            size="small"
                            onClick={() => onRemove(upload.file.key)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {isActive && (
                <Box sx={{ mt: 1.25 }}>
                    <LinearProgress
                        variant={
                            upload.status === "queued"
                                ? "indeterminate"
                                : "determinate"
                        }
                        value={progress}
                        sx={{
                            height: 7,
                            borderRadius: 5,
                            backgroundColor: primaryTint,
                        }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            mt: 0.6,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {upload.status === "queued"
                                ? "Queued"
                                : "Upload in progress"}
                        </Typography>
                        {upload.status === "uploading" && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 700 }}
                            >
                                {progress}%
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
}

export default function UploadsMenu() {
    const uploads = useUploads();
    const [open, setOpen] = useState(false);

    const visibleUploads = useMemo(
        () => uploads.filter((upload) => upload.show === true),
        [uploads],
    );
    const activeUploads = useMemo(
        () =>
            visibleUploads.filter((upload) =>
                activeStatuses.has(upload.status),
            ),
        [visibleUploads],
    );
    const finishedUploads = useMemo(
        () =>
            visibleUploads.filter((upload) =>
                finishedStatuses.has(upload.status),
            ),
        [visibleUploads],
    );
    const latestUpload = visibleUploads[visibleUploads.length - 1];
    const hasQueuedUploads = activeUploads.some(
        (upload) => upload.status === "queued",
    );
    const overallProgress =
        activeUploads.length > 0
            ? Math.round(
                  activeUploads.reduce(
                      (sum, upload) => sum + (upload.progress || 0),
                      0,
                  ) / activeUploads.length,
              )
            : 0;

    let summary = "Uploads";

    if (activeUploads.length === 1) {
        summary =
            activeUploads[0].status === "queued"
                ? "Upload queued"
                : `Uploading ${activeUploads[0].progress || 0}%`;
    } else if (activeUploads.length > 1) {
        summary = `${activeUploads.length} uploads`;
    } else if (latestUpload?.status === "complete") {
        summary = "Upload complete";
    } else if (latestUpload?.status === "error") {
        summary = "Upload failed";
    }

    if (visibleUploads.length === 0) {
        return null;
    }

    const clearFinishedUploads = () => {
        finishedUploads.forEach((upload) => {
            uploadManager.dismiss(upload.file.key);
        });
    };

    return (
        <>
            <Tooltip title="Open uploads">
                <ButtonBase
                    aria-label={summary}
                    onClick={() => setOpen(true)}
                    sx={{
                        minHeight: 40,
                        px: { xs: 0.75, sm: 1.15 },
                        borderRadius: 5,
                        color: "rgba(255, 255, 255, 0.96)",
                        backgroundColor:
                            activeUploads.length > 0
                                ? "rgba(255, 255, 255, 0.13)"
                                : "transparent",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.18)",
                        },
                    }}
                >
                    <Badge
                        badgeContent={
                            activeUploads.length > 1
                                ? activeUploads.length
                                : 0
                        }
                        color="secondary"
                    >
                        <Box
                            sx={{
                                position: "relative",
                                width: 32,
                                height: 32,
                                display: "grid",
                                placeItems: "center",
                            }}
                        >
                            {activeUploads.length > 0 && (
                                <CircularProgress
                                    variant={
                                        hasQueuedUploads
                                            ? "indeterminate"
                                            : "determinate"
                                    }
                                    value={overallProgress}
                                    size={32}
                                    thickness={3}
                                    sx={{
                                        position: "absolute",
                                        color: "rgba(255, 255, 255, 0.9)",
                                    }}
                                />
                            )}
                            {activeUploads.length > 0 ? (
                                <CloudUploadOutlinedIcon fontSize="small" />
                            ) : latestUpload?.status === "complete" ? (
                                <CheckCircleOutlineIcon fontSize="small" />
                            ) : latestUpload?.status === "error" ? (
                                <ErrorOutlineIcon fontSize="small" />
                            ) : (
                                <CloudUploadOutlinedIcon fontSize="small" />
                            )}
                        </Box>
                    </Badge>
                    <Typography
                        variant="body2"
                        sx={{
                            display: { xs: "none", md: "block" },
                            ml: 0.75,
                            mr: 0.25,
                            maxWidth: 150,
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {summary}
                    </Typography>
                </ButtonBase>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
                aria-labelledby="uploads-dialog-title"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <CloudUploadOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="uploads-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Uploads
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Current uploads and recent activity.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close uploads"
                        onClick={() => setOpen(false)}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    {activeUploads.length > 0 && (
                        <Box sx={{ mb: finishedUploads.length > 0 ? 2 : 0 }}>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{
                                    display: "block",
                                    mb: 0.75,
                                    fontWeight: 800,
                                }}
                            >
                                In progress
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {activeUploads.map((upload) => (
                                    <UploadRow
                                        key={upload.file.key}
                                        upload={upload}
                                        onCancel={uploadManager.abort}
                                        onRetry={uploadManager.retry}
                                        onRemove={uploadManager.dismiss}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {finishedUploads.length > 0 && (
                        <Box>
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{
                                    display: "block",
                                    mb: 0.75,
                                    fontWeight: 800,
                                }}
                            >
                                Recent
                            </Typography>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                }}
                            >
                                {finishedUploads.map((upload) => (
                                    <UploadRow
                                        key={upload.file.key}
                                        upload={upload}
                                        onCancel={uploadManager.abort}
                                        onRetry={uploadManager.retry}
                                        onRemove={uploadManager.dismiss}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        color="inherit"
                        startIcon={<DeleteSweepOutlinedIcon />}
                        onClick={clearFinishedUploads}
                        disabled={finishedUploads.length === 0}
                    >
                        Clear finished
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => setOpen(false)}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
